import { DefaultButton, getTheme, Icon, Stack } from "@fluentui/react";

import { DefaultCardTitle } from "../styles";

import DocumentUploadTable from "./DocumentUploadTable";

import { Card } from "@/components/Shared/Card";
import { useGet } from "@/core/libs/api/react-query/hook/use-get";
import { AttachmentTypes } from "@/modules/Contracts/fields/fields";

interface ContractDocumentsProps {
  contractId: number;
  canSendDocument?: boolean;
  setIsVisible?: (condition: boolean) => void;
  isSendingDocumentStep: boolean;
}

interface ContractUpload {
  id: number;
  uploads: Array<Upload>;
}

interface UploadDocumentsButtonProps {
  onClick: () => void;
}

interface EmptyUploadsStateProps {
  canSendDocument?: boolean;
  onUploadClick?: () => void;
}

export interface Upload {
  id: number;
  fileName: string;
  filePath: string;
  createdAt: string;
  attachmentType: AttachmentTypes;
}

interface UploadsContentProps {
  uploads: Array<Upload>;
  isSendingDocumentStep: boolean;
}

function UploadsContent({
  uploads,
  isSendingDocumentStep
}: UploadsContentProps): JSX.Element {
  return (
    <>
      <DocumentUploadTable uploads={uploads} />

      {isSendingDocumentStep && (
        <Stack
          tokens={{ padding: 20 }}
          horizontalAlign="center"
          verticalAlign="center"
        />
      )}
    </>
  );
}

function ContractDocumentsTitle(): JSX.Element {
  return (
    <DefaultCardTitle>
      <Icon iconName="Attach" style={{ marginRight: 10 }} />
      Anexos
    </DefaultCardTitle>
  );
}

function EmptyUploadsState({
  canSendDocument,
  onUploadClick
}: EmptyUploadsStateProps): JSX.Element {
  return (
    <Stack
      tokens={{ padding: 20 }}
      horizontalAlign="center"
      verticalAlign="center"
      verticalFill
    >
      <Icon
        iconName="DocumentSearch"
        style={{ fontSize: 80, color: "#6b6b6b" }}
      />

      <span
        style={{
          fontSize: 20,
          color: "#6b6b6b",
          textAlign: "center"
        }}
      >
        Nenhum anexo foi encontrado.
      </span>

      {canSendDocument && onUploadClick && (
        <UploadDocumentsButton onClick={onUploadClick} />
      )}
    </Stack>
  );
}

function UploadDocumentsButton({
  onClick
}: UploadDocumentsButtonProps): JSX.Element {
  const theme = getTheme();

  return (
    <DefaultButton
      id="upload-documents-button"
      style={{ marginTop: 15 }}
      onClick={onClick}
      styles={{
        root: { borderColor: theme.palette.themeDark },
        flexContainer: { color: theme.palette.themeDark }
      }}
    >
      <Icon
        iconName="CloudUpload"
        style={{
          marginRight: 10,
          fontSize: 18
        }}
      />
      Anexar documento(s)
    </DefaultButton>
  );
}

export function ContractDocuments({
  contractId,
  canSendDocument,
  isSendingDocumentStep,
  setIsVisible
}: ContractDocumentsProps): JSX.Element {
  const { data: contractUploads } = useGet<ContractUpload>({
    url: `contracts/${contractId}/uploads`,
    queryToken: "contract-uploads"
  });

  const hasUploads = Boolean(contractUploads?.uploads.length);

  return (
    <Stack verticalFill>
      <ContractDocumentsTitle />

      <Card borderRadius="1rem" padding="1rem">
        <Stack style={{ height: "100%", minHeight: 250 }}>
          <Stack.Item styles={{ root: { height: "100%" } }}>
            {hasUploads ? (
              <UploadsContent
                uploads={contractUploads.uploads}
                isSendingDocumentStep={isSendingDocumentStep}
              />
            ) : (
              <EmptyUploadsState
                canSendDocument={canSendDocument}
                onUploadClick={
                  setIsVisible ? () => setIsVisible(true) : undefined
                }
              />
            )}
          </Stack.Item>
        </Stack>
      </Card>
    </Stack>
  );
}
